/** Doluluk senaryoları: kullanıcı kimliği ↔ iç hesap / grid anahtarı. */

export const SCENARIO_OPTIONS = [
  {
    id: "asim_0",
    beta: 0,
    label: "Aşım yok",
    short: "%0",
    resultKey: "method4_beta0",
    gridKey: "method4_0",
  },
  {
    id: "asim_10",
    beta: 0.1,
    label: "%10 aşım",
    short: "%10",
    resultKey: "method4_beta10",
    gridKey: "method4_10",
  },
  {
    id: "asim_20",
    beta: 0.2,
    label: "%20 aşım",
    short: "%20",
    resultKey: "method4_beta20",
    gridKey: "method4_20",
  },
  {
    id: "asim_30",
    beta: 0.3,
    label: "%30 aşım",
    short: "%30",
    resultKey: "method4_beta30",
    gridKey: "method4_30",
  },
];

export const CURRENT_SCENARIO = {
  id: "mevcut",
  beta: null,
  label: "Mevcut plan",
  short: "Mevcut",
  resultKey: "method6",
  gridKey: "method6",
};

export const DEFAULT_SCENARIOS = ["asim_0", "asim_20"];

const BY_ID = Object.fromEntries([...SCENARIO_OPTIONS, CURRENT_SCENARIO].map((s) => [s.id, s]));

const FROM_LEGACY = {};
for (const s of [...SCENARIO_OPTIONS, CURRENT_SCENARIO]) {
  FROM_LEGACY[s.id] = s.id;
  FROM_LEGACY[s.resultKey] = s.id;
  FROM_LEGACY[s.gridKey] = s.id;
}

export function scenarioById(id) {
  return BY_ID[id] || null;
}

export function scenarioLabel(id) {
  return scenarioById(id)?.label || String(id || "");
}

export function scenarioFromLegacyKey(key) {
  if (key == null || key === "") return null;
  const metin = String(key).trim();
  if (FROM_LEGACY[metin]) return FROM_LEGACY[metin];
  if (["method1", "method2", "method3", "method5"].includes(metin.toLowerCase())) {
    return "asim_0";
  }
  const n = Number(metin.replace(",", "."));
  if (n === 0) return "asim_0";
  if (n === 10 || n === 0.1) return "asim_10";
  if (n === 20 || n === 0.2) return "asim_20";
  if (n === 30 || n === 0.3) return "asim_30";
  return null;
}

export function scenariosFromLegacyMethods(methods) {
  const ids = [];
  const seen = new Set();
  for (const m of methods || []) {
    const id = scenarioFromLegacyKey(m);
    if (id && id !== CURRENT_SCENARIO.id && !seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  }
  if (!ids.length) return [...DEFAULT_SCENARIOS];
  return SCENARIO_OPTIONS.filter((s) => ids.includes(s.id)).map((s) => s.id);
}

export function resultKeysOf(ids) {
  return (ids || []).map((id) => scenarioById(id)?.resultKey).filter(Boolean);
}

export function requestScenarioIds(raw) {
  if (raw == null || raw === "") return [...DEFAULT_SCENARIOS];
  const list = Array.isArray(raw) ? raw : [raw];
  return scenariosFromLegacyMethods(list);
}

export function ledScenarios(ids) {
  const selected = requestScenarioIds(ids);
  return [CURRENT_SCENARIO, ...SCENARIO_OPTIONS.filter((s) => selected.includes(s.id))];
}

export function isExplicitClassic(source) {
  const raw = String(source?.type_policy ?? source?.typePolicy ?? "")
    .trim()
    .toLowerCase();
  return raw === "classic";
}

export const ALLOWED_BETA_PCTS = [0, 10, 20, 30];
export const ALLOWED_BETAS = [0, 0.1, 0.2, 0.3];

/**
 * KPI kartı / maliyet çekmecesi gibi sunum bileşenleri için etkin senaryo id'si.
 * MethodRadioBar ve FrequencyDetailKpiCard'daki aynı çözümleme mantığı (tekilleştirilmiş).
 */
export function resolveActiveScenarioId({ activeScenarioId, currentMethod, selectedBeta }) {
  if (activeScenarioId) return activeScenarioId;
  if (currentMethod === 6) return CURRENT_SCENARIO.id;
  const pct = Math.round(Number(selectedBeta) * 100);
  return ALLOWED_BETA_PCTS.includes(pct) ? `asim_${pct}` : "asim_0";
}

/**
 * resolveActiveScenarioId ham β/method matematiğinden üretir; bu id, Karşılaştır
 * segmentinde gerçekten sunulan seçeneklerde (availableIds) olmayabilir (örn. β=0.3
 * kalan bir analiz state'inden geliyor ama segment yalnızca Mevcut/%0/%20 sunuyor).
 * Böyle durumda çekmece/tablo var olmayan bir senaryo bloğu okur ("—"/0 görünür).
 * Bu yüzden her zaman segmentte GERÇEKTEN gösterilen bir id'ye kenetle.
 */
export function clampScenarioId(id, availableIds) {
  const list = Array.isArray(availableIds) ? availableIds : [];
  if (list.includes(id)) return id;
  if (list.includes("asim_0")) return "asim_0";
  if (list.includes(CURRENT_SCENARIO.id)) return CURRENT_SCENARIO.id;
  return list[0] ?? CURRENT_SCENARIO.id;
}

export const OCCUPANCY_MIN = 50;
export const OCCUPANCY_MAX = 100;
export const OCCUPANCY_DEFAULT = 85;

export function clampOccupancy(value, fallback = OCCUPANCY_DEFAULT) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(OCCUPANCY_MAX, Math.max(OCCUPANCY_MIN, Math.round(n)));
}

export function occupancyOutOfRange(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return false;
  return n < OCCUPANCY_MIN || n > OCCUPANCY_MAX;
}

export function estimateNetworkMinutes(hatCount, parallel = 4, secondsPerHat = 12) {
  const n = Math.max(0, Number(hatCount) || 0);
  const p = Math.max(1, Number(parallel) || 4);
  const s = Math.max(1, Number(secondsPerHat) || 12);
  if (!n) return 0;
  return Math.max(1, Math.ceil((n * s) / p / 60));
}
